'use client';

import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { 
  useTrackPageView, 
  useTrackGameLifecycle, 
  useTrackConversionStep, 
  useTrackFeatureUsage, 
  useTrackErrorRecovery,
  type GameLifecycleEvent,
  type ConversionFunnelStep
} from '@/providers/ObservabilityProvider';

/**
 * Hook for automatic page tracking with Next.js router integration
 */
export const usePageTracking = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const trackPageView = useTrackPageView();

  useEffect(() => {
    if (pathname === '/tv/direct') return;
    // Track page view when pathname changes
    const fullPath = searchParams.toString() ? `${pathname}?${searchParams.toString()}` : pathname;
    trackPageView(fullPath);
  }, [pathname, searchParams, trackPageView]);
};

/**
 * Hook for game-specific RUM tracking
 */
export const useGameRUM = () => {
  const trackGameLifecycle = useTrackGameLifecycle();
  const trackFeatureUsage = useTrackFeatureUsage();

  const trackRoomCreate = (gameType: string, metadata?: Record<string, any>) => {
    trackGameLifecycle({
      gameType,
      action: 'room_create',
      metadata
    });
  };

  const trackRoomJoin = (gameType: string, roomId: string, participantCount?: number, metadata?: Record<string, any>) => {
    trackGameLifecycle({
      gameType,
      action: 'room_join',
      roomId,
      participantCount,
      metadata
    });
  };

  const trackRoomLeave = (gameType: string, roomId: string, duration?: number, metadata?: Record<string, any>) => {
    trackGameLifecycle({
      gameType,
      action: 'room_leave',
      roomId,
      duration,
      metadata
    });
  };

  const trackGameStart = (gameType: string, roomId: string, participantCount?: number, metadata?: Record<string, any>) => {
    trackGameLifecycle({
      gameType,
      action: 'game_start',
      roomId,
      participantCount,
      metadata
    });
  };

  const trackGameEnd = (gameType: string, roomId: string, duration?: number, metadata?: Record<string, any>) => {
    trackGameLifecycle({
      gameType,
      action: 'game_end',
      roomId,
      duration,
      metadata
    });
  };

  const trackGameFeature = (featureName: string, action: string, metadata?: Record<string, any>) => {
    trackFeatureUsage(featureName, action, metadata);
  };

  return {
    trackRoomCreate,
    trackRoomJoin,
    trackRoomLeave,
    trackGameStart,
    trackGameEnd,
    trackGameFeature
  };
};

/**
 * Hook for conversion funnel tracking
 */
export const useConversionTracking = () => {
  const trackConversionStep = useTrackConversionStep();

  const trackOnboardingStep = (step: string, success: boolean, duration?: number, errorReason?: string) => {
    trackConversionStep({
      step,
      funnel: 'onboarding',
      success,
      duration,
      errorReason
    });
  };

  const trackRoomCreationStep = (step: string, success: boolean, duration?: number, errorReason?: string) => {
    trackConversionStep({
      step,
      funnel: 'room_creation',
      success,
      duration,
      errorReason
    });
  };

  const trackGameParticipationStep = (step: string, success: boolean, duration?: number, errorReason?: string) => {
    trackConversionStep({
      step,
      funnel: 'game_participation',
      success,
      duration,
      errorReason
    });
  };

  return {
    trackOnboardingStep,
    trackRoomCreationStep,
    trackGameParticipationStep
  };
};

/**
 * Hook for error recovery tracking
 */
export const useErrorRecoveryTracking = () => {
  const trackErrorRecovery = useTrackErrorRecovery();

  const trackSTTErrorRecovery = (recoveryAction: string, success: boolean, metadata?: Record<string, any>) => {
    trackErrorRecovery('stt_error', recoveryAction, success, metadata);
  };

  const trackWebRTCErrorRecovery = (recoveryAction: string, success: boolean, metadata?: Record<string, any>) => {
    trackErrorRecovery('webrtc_error', recoveryAction, success, metadata);
  };

  const trackNetworkErrorRecovery = (recoveryAction: string, success: boolean, metadata?: Record<string, any>) => {
    trackErrorRecovery('network_error', recoveryAction, success, metadata);
  };

  const trackGeneralErrorRecovery = (errorType: string, recoveryAction: string, success: boolean, metadata?: Record<string, any>) => {
    trackErrorRecovery(errorType, recoveryAction, success, metadata);
  };

  return {
    trackSTTErrorRecovery,
    trackWebRTCErrorRecovery,
    trackNetworkErrorRecovery,
    trackGeneralErrorRecovery
  };
};

/**
 * Hook for feature usage tracking
 */
export const useFeatureTracking = () => {
  const trackFeatureUsage = useTrackFeatureUsage();

  const trackButtonClick = (buttonName: string, context?: string, metadata?: Record<string, any>) => {
    trackFeatureUsage('button_interaction', 'click', {
      buttonName,
      context,
      ...metadata
    });
  };

  const trackModalOpen = (modalName: string, trigger?: string, metadata?: Record<string, any>) => {
    trackFeatureUsage('modal_interaction', 'open', {
      modalName,
      trigger,
      ...metadata
    });
  };

  const trackModalClose = (modalName: string, method?: string, duration?: number, metadata?: Record<string, any>) => {
    trackFeatureUsage('modal_interaction', 'close', {
      modalName,
      method,
      duration,
      ...metadata
    });
  };

  const trackFormSubmission = (formName: string, success: boolean, duration?: number, metadata?: Record<string, any>) => {
    trackFeatureUsage('form_interaction', 'submit', {
      formName,
      success,
      duration,
      ...metadata
    });
  };

  const trackSearchUsage = (searchTerm: string, resultsCount?: number, metadata?: Record<string, any>) => {
    trackFeatureUsage('search_interaction', 'search', {
      searchTerm: searchTerm ? 'present' : 'empty', // Privacy conscious
      resultsCount,
      ...metadata
    });
  };

  const trackNavigationUsage = (navigationItem: string, destination: string, metadata?: Record<string, any>) => {
    trackFeatureUsage('navigation_interaction', 'navigate', {
      navigationItem,
      destination,
      ...metadata
    });
  };

  return {
    trackButtonClick,
    trackModalOpen,
    trackModalClose,
    trackFormSubmission,
    trackSearchUsage,
    trackNavigationUsage,
    trackCustomFeature: trackFeatureUsage
  };
};

/**
 * Combined hook that provides all RUM tracking capabilities
 */
export const useRUM = () => {
  const gameRUM = useGameRUM();
  const conversionTracking = useConversionTracking();
  const errorRecoveryTracking = useErrorRecoveryTracking();
  const featureTracking = useFeatureTracking();

  return {
    ...gameRUM,
    ...conversionTracking,
    ...errorRecoveryTracking,
    ...featureTracking
  };
};

// Export types for external use
export type { GameLifecycleEvent, ConversionFunnelStep }; 